import * as I from 'immutable';
import React from 'react';
import {Navigate} from 'react-router-dom';

import {OverlaysProvider} from 'app/components/Library/OverlaysProvider';
import {usePushNotification} from 'app/components/Notification';
import {ApiOrganization, ApiOrganizationUser} from 'app/modules/Remote/Organization';
import {LoggedInUserActions} from 'app/providers/AuthProvider';
import featureFlags from 'app/utils/featureFlags';
import * as routeUtils from 'app/utils/routeUtils';

import ManageFeaturesOverlay from './ManageFeaturesOverlay';
import ManageFeaturesProvider from './ManageFeaturesProvider';
import ManageFeaturesSelectMode from './ManageFeaturesSelectMode';
import ManagePropertiesAgent from './ManagePropertiesAgent';
import ManagePropertiesDraw from './ManagePropertiesDraw';
import ManagePropertiesSelectParcels from './ManagePropertiesSelectParcels';
import ManagePropertiesUpload from './ManagePropertiesUpload';
import ManageUpdateOverlay from './ManageUpdateOverlay';

const MANAGE_PROPERTIES_MODES = [
  'upload',
  'draw',
  'parcel',
  'select',
  'overlay',
  'updateOverlay',
  'agentUpload',
];
export type ManagePropertiesMode = (typeof MANAGE_PROPERTIES_MODES)[number];

export function isManagePropertyModeOption(value: string): boolean {
  return MANAGE_PROPERTIES_MODES.includes(value);
}

export function isDrawnPropertiesMode(mode: ManagePropertiesMode): boolean {
  return mode === 'draw' || mode === 'parcel' || mode === 'agentUpload';
}

export interface Props {
  profile: I.ImmutableOf<ApiOrganizationUser>;
  organization: I.MapAsRecord<I.ImmutableFields<ApiOrganization>>;
  loggedInUserActions: LoggedInUserActions;
  projectId?: string;
  mode: ManagePropertiesMode;
  firebaseToken: string | null;
}
const ManageFeatures: React.FunctionComponent<React.PropsWithChildren<Props>> = ({
  profile,
  organization,
  loggedInUserActions,
  projectId,
  mode,
  firebaseToken,
}) => {
  const orgIdPrefix = routeUtils.makeUuidPrefix(organization.get('id'));
  const pushNotification = usePushNotification();

  if (profile.get('role') == 'readonly') {
    pushNotification({
      message: 'You do not have authorization to manage properties for this organization.',
      autoHideDuration: 4000,
    });

    return <Navigate to={`/${orgIdPrefix}/projects`} replace />;
  }

  const renderManageFeaturesView = () => {
    switch (mode) {
      case 'draw':
        return (
          <ManagePropertiesDraw
            profile={profile}
            organization={organization}
            loggedInUserActions={loggedInUserActions}
            firebaseToken={firebaseToken}
          />
        );
      case 'parcel':
        return (
          <OverlaysProvider organization={organization}>
            <ManagePropertiesSelectParcels
              profile={profile}
              organization={organization}
              loggedInUserActions={loggedInUserActions}
              firebaseToken={firebaseToken}
            />
          </OverlaysProvider>
        );
      case 'upload':
        return (
          <ManagePropertiesUpload
            profile={profile}
            organization={organization}
            loggedInUserActions={loggedInUserActions}
          />
        );
      case 'agentUpload':
        if (featureFlags.AGENT_ONBOARDING(organization, profile)) {
          return (
            <ManagePropertiesAgent
              profile={profile}
              organization={organization}
              loggedInUserActions={loggedInUserActions}
            />
          );
        } else {
          return (
            <Navigate
              to={`/${orgIdPrefix}/admin/manageProperties/select/${projectId || ''}`}
              replace
            />
          );
        }
      case 'overlay':
        return (
          <OverlaysProvider organization={organization}>
            <ManageFeaturesOverlay
              profile={profile}
              organization={organization}
              loggedInUserActions={loggedInUserActions}
            />
          </OverlaysProvider>
        );
      case 'updateOverlay': {
        const fcId = parseInt(projectId!);
        if (isNaN(fcId)) {
          return <Navigate to={`/${orgIdPrefix}/settings/overlays`} replace />;
        }
        return (
          <OverlaysProvider organization={organization}>
            <ManageUpdateOverlay
              overlayFeatureCollectionId={fcId}
              profile={profile}
              organization={organization}
              loggedInUserActions={loggedInUserActions}
              firebaseToken={firebaseToken}
            />
          </OverlaysProvider>
        );
      }
      case 'select':
        return (
          <ManageFeaturesSelectMode
            profile={profile}
            organization={organization}
            loggedInUserActions={loggedInUserActions}
            projectId={projectId || ''}
          />
        );
      default:
        <ManageFeaturesSelectMode
          profile={profile}
          organization={organization}
          loggedInUserActions={loggedInUserActions}
          projectId={projectId || ''}
        />;
    }
  };

  return (
    <ManageFeaturesProvider organization={organization} mode={mode} projectId={projectId}>
      {renderManageFeaturesView()}
    </ManageFeaturesProvider>
  );
};

export default ManageFeatures;
